import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import type {AutoTierOption} from '@github-ui/copilot-shared-ui/components/chat-model-picker/ModelPickerDesign'
import {useEffect, useSyncExternalStore} from 'react'

export const COPILOT_FUSION_DISPLAY_NAME = 'Copilot Fusion'

export interface FusionAutoTiers {
  options: AutoTierOption[]
  defaultTier: string
}

/** github-app's fallback while CAPI's catalog is unavailable: only Standard can be sent. */
export const FUSION_FALLBACK_AUTO_TIERS: FusionAutoTiers = {
  defaultTier: 'balance',
  options: [
    {id: 'balance', label: 'Standard', description: 'Prioritizes versatile models for efficient everyday coding.'},
    {
      id: 'hydrafusion',
      label: 'High',
      description: 'Prioritizes capable models for challenging tasks and deeper reasoning.',
      disabled: true,
    },
    {
      id: 'hydrafusion-max',
      label: 'Max',
      description: 'Prioritizes the most capable models for the most demanding tasks and complex reasoning.',
      disabled: true,
    },
  ],
}

interface AutoTierDescriptor {
  id?: unknown
  display_name?: unknown
  displayName?: unknown
  description?: unknown
  type?: unknown
  status?: {enabled?: unknown; message?: unknown; tier_picker_enabled?: unknown; pickerVisible?: unknown}
}

/**
 * Picker tiers from an Auto tier catalog: CAPI `GET /meta`'s snake_case `auto` field, or the
 * host's camelCase `getAutoTiers` result built from it. Mirrors github-app: `auto`/`fusion`
 * tiers the picker may show, minus `fast`. `undefined` unless the catalog lists a Fusion tier
 * and at least one tier is enabled.
 */
export function parseFusionAutoTiers(catalog: unknown): FusionAutoTiers | undefined {
  const auto = catalog as {default_tier?: unknown; defaultTier?: unknown; tiers?: unknown} | null | undefined
  const catalogDefault = auto?.default_tier ?? auto?.defaultTier
  if (typeof catalogDefault !== 'string' || !Array.isArray(auto?.tiers)) return undefined

  const validTiers = (auto.tiers as Array<AutoTierDescriptor | null>).filter(
    (tier): tier is AutoTierDescriptor =>
      typeof tier === 'object' &&
      tier !== null &&
      typeof tier.id === 'string' &&
      /^[a-z0-9][a-z0-9._-]{0,63}$/i.test(tier.id) &&
      tier.id !== 'fast' &&
      (tier.type === 'auto' || tier.type === 'fusion') &&
      typeof tier.status?.enabled === 'boolean',
  )
  // A Fusion runtime lists its Fusion tiers even when the plan disables or hides them.
  if (!validTiers.some(tier => tier.type === 'fusion')) return undefined
  const tiers = validTiers.filter(tier => (tier.status?.tier_picker_enabled ?? tier.status?.pickerVisible) !== false)

  const options = tiers.map(tier => {
    const enabled = tier.status?.enabled === true
    const label = tier.display_name ?? tier.displayName
    const description = typeof tier.description === 'string' ? tier.description : ''
    const message = typeof tier.status?.message === 'string' ? tier.status.message : undefined
    return {
      id: tier.id as string,
      label: typeof label === 'string' ? label : (tier.id as string),
      description: enabled ? description : message || description,
      ...(enabled ? {} : {disabled: true}),
    }
  })
  const defaultTier = options.find(option => option.id === catalogDefault && !option.disabled)?.id
  const firstEnabled = options.find(option => !option.disabled)?.id
  return defaultTier || firstEnabled ? {options, defaultTier: (defaultTier ?? firstEnabled) as string} : undefined
}

// One catalog per page, shared by every composer surface (/copilot, embedded, repository agents).
let catalog: FusionAutoTiers | undefined
let requested = false
const listeners = new Set<() => void>()

async function loadCatalog() {
  if (requested) return
  requested = true
  try {
    const response = await fetchCAPI({path: '/meta', method: 'GET', useCapi: true})
    if (!response.ok) throw new Error(`/meta returned ${response.status}`)
    catalog = parseFusionAutoTiers(((await response.json()) as {auto?: unknown} | null)?.auto)
  } catch {
    catalog = undefined
    // Let a later composer retry after a failed request.
    requested = false
  }
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function resetFusionAutoTiersForTests() {
  catalog = undefined
  requested = false
}

/** Copilot Fusion's tiers when `enabled`: CAPI's catalog once loaded, else the fallback. */
export function useFusionAutoTiers(enabled: boolean): FusionAutoTiers | undefined {
  const current = useSyncExternalStore(
    subscribe,
    () => catalog,
    () => undefined,
  )

  useEffect(() => {
    if (enabled) void loadCatalog()
  }, [enabled])

  return enabled ? (current ?? FUSION_FALLBACK_AUTO_TIERS) : undefined
}
